import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { config } from "../app.config";
import { AuthApi } from "./api";
import { clearAuthSession, setAuthSession } from "./auth";
import { Configuration } from "./configuration";
import { invalidateMetabaseQueriesAfterTokenRefresh } from "./queryClientRegistry";

export const axiosInstance = axios.create({
  baseURL: config.api.baseUrl,
  headers: {
    "Content-Type": "application/json",
  },
});

axiosInstance.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

let isRefreshing = false;
let failedQueue: QueuedRequest[] = [];

interface QueuedRequest {
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}

const processQueue = (error: unknown, token: string | null = null) => {
  for (const prom of failedQueue) {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token as string);
    }
  }
  failedQueue = [];
};

const refreshAxiosInstance = axios.create({
  baseURL: config.api.baseUrl,
  headers: {
    "Content-Type": "application/json",
  },
});

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    const isLoginRequest = originalRequest.url?.includes("/auth/login");
    const isRefreshRequest = originalRequest.url?.includes("/auth/refresh");

    if (
      error.response?.status !== 401 ||
      originalRequest._retry ||
      isLoginRequest ||
      isRefreshRequest
    ) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axiosInstance(originalRequest);
        })
        .catch((err) => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const refreshToken = localStorage.getItem("mun-auth-rtoken");
    if (!refreshToken) {
      const error = new Error("No refresh token available");
      processQueue(error);
      clearAuthSession();
      window.location.href = "/login";
      return Promise.reject(error);
    }

    try {
      const authApi = new AuthApi(apiConfig, undefined, refreshAxiosInstance);
      const response = await authApi.refreshToken({ refreshToken });

      setAuthSession({
        access_token: response.data.accessToken,
        refresh_token: response.data.refreshToken,
      });

      const { accessToken } = response.data;
      axiosInstance.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
      originalRequest.headers.Authorization = `Bearer ${accessToken}`;

      processQueue(null, accessToken);
      invalidateMetabaseQueriesAfterTokenRefresh();
      return axiosInstance(originalRequest);
    } catch (refreshError) {
      console.error("Token refresh failed:", refreshError);
      clearAuthSession();
      window.location.href = "/login";
      processQueue(refreshError);
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export const apiConfig = new Configuration({
  basePath: config.api.baseUrl,
  accessToken: () => localStorage.getItem("mun-auth-token") || "",
  baseOptions: {
    headers: {
      "Content-Type": "application/json",
    },
  },
});

export { axiosInstance as api };
